/* Restores the Online Store cart after a temporary sample-only checkout. */
(() => {
  const root = window.Shopify?.routes?.root || '/';
  const key = 'bogart_saved_furniture_cart_v1';
  const checkoutKey = 'bogart_sample_checkout_session_v1';
  let busy = false;


  const labels = () => ({ error: window.themeVariables.strings.sampleRestoreError, changed: window.themeVariables.strings.sampleRestoreChanged });
  const read = () => JSON.parse(localStorage.getItem(key) || 'null');

  async function request(path, body) {
    const response = await fetch(`${root}${path}`, body === undefined
      ? { cache: 'no-store', headers: { Accept: 'application/json' } }
      : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
    const result = await response.json();
    if (!response.ok) throw new Error(result.description || labels().error);
    return result;
  }

  function items(cart) {
    return cart.items.map((item) => ({
      id: item.variant_id || item.id, quantity: item.quantity, properties: item.properties || {},
      ...(item.selling_plan_allocation ? { selling_plan: item.selling_plan_allocation.selling_plan.id } : {}),
    }));
  }

  function signature(lines) {
    const totals = new Map();
    lines.forEach((item) => {
      const properties = Object.entries(item.properties || {})
        .filter(([name]) => !['_stock_status', '_lead_days'].includes(name))
        .sort(([a], [b]) => a.localeCompare(b));
      const id = JSON.stringify([String(item.id), item.selling_plan || null, properties]);
      totals.set(id, (totals.get(id) || 0) + item.quantity);
    });
    return JSON.stringify([...totals].sort(([a], [b]) => a.localeCompare(b)));
  }

  async function exclusive(action) {
    if (busy) return;
    busy = true;
    try {
      if (window.navigator?.locks) return await window.navigator.locks.request(key, action);
      return await action();
    } finally { busy = false; }
  }

  async function restore() {
    return exclusive(async () => {
      const backup = read();
      if (!backup) return;
      const current = await request('cart.js');
      if (signature(items(current)) !== signature(backup.items)) {
        const state = window.ATFabricSampleCart.inspect(await window.ATFabricSampleCart.getCart());
        // A partially restored or independently edited furniture cart must not be overwritten or doubled.
        if (state.regularQuantity) throw new Error(labels().changed);
        if (signature(items(await request('cart.js'))) !== signature(items(current))) throw new Error(labels().changed);
        if (current.items.length) await request('cart/clear.js', {});
        await request('cart/add.js', { items: backup.items });
        if (signature(items(await request('cart.js'))) !== signature(backup.items)) throw new Error(labels().error);
      }
      const latest = await request('cart.js');
      await request('cart/update.js', { note: backup.note, attributes: {
        ...Object.fromEntries(Object.keys(latest.attributes || {}).map((name) => [name, ''])), ...backup.attributes,
      }, discount: backup.discounts.join(',') });
      localStorage.removeItem(key);
      localStorage.removeItem(checkoutKey);
      refreshNotices();
      window.location.href = `${root}cart`;
    });
  }

  async function discardTemporarySamples(checkout) {
    return exclusive(async () => {
      const updates = Object.fromEntries((checkout.ids || []).map((id) => [String(id), 0]));
      if (Object.keys(updates).length) await request('cart/update.js', { updates });

      const current = await request('cart.js');
      if ((checkout.ids || []).some((id) => current.items.some((item) => String(item.variant_id || item.id) === String(id)))) {
        throw new Error(labels().error);
      }

      if (!current.items.length) {
        await request('cart/update.js', {
          note: '',
          attributes: Object.fromEntries(Object.keys(current.attributes || {}).map((name) => [name, ''])),
          discount: '',
        });
      }

      localStorage.removeItem(checkoutKey);
      refreshNotices();
      window.location.reload();
    });
  }

  function refreshNotices() {
    document.querySelectorAll('saved-furniture-notice').forEach((notice) => notice.refresh());
  }

  async function onStorefrontReturn() {
    if (busy) return;
    try {
      const checkout = JSON.parse(localStorage.getItem(checkoutKey) || 'null');
      const backup = read();
      if (checkout && backup) return await restore();
      if (checkout) return await discardTemporarySamples(checkout);
      refreshNotices();
    } catch { /* Storage restrictions must not block ordinary shopping. */ }
  }

  class SavedFurnitureNotice extends HTMLElement {
    connectedCallback() {
      this.refresh();
      const button = this.querySelector('[data-restore-furniture]');
      button.onclick = async () => {
        if (busy) return;
        button.disabled = true;
        const error = this.querySelector('[data-restore-error]');
        error.hidden = true;
        try { await restore(); refreshNotices(); }
        catch (failure) { error.textContent = `${labels().error} ${failure.message}`; error.hidden = false; }
        finally { button.disabled = false; }
      };
    }

    refresh() {
      try { this.hidden = !read() || Boolean(localStorage.getItem(checkoutKey)); } catch { this.hidden = true; }
    }
  }

  if (!window.customElements.get('saved-furniture-notice')) {
    window.customElements.define('saved-furniture-notice', SavedFurnitureNotice);
  }

  window.addEventListener('pageshow', onStorefrontReturn);
  window.addEventListener('storage', (event) => {
    if (event.key === key || event.key === checkoutKey || event.key === null) refreshNotices();
  });
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', onStorefrontReturn, { once: true });
  } else {
    setTimeout(onStorefrontReturn, 0);
  }
  window.ATLegacySampleCart = { restore, onStorefrontReturn };
})();
